/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from 'react';

// reactstrap components
import {
  Spinner,
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Alert,
  Table,
} from 'reactstrap';
// core components
import utils from '../utils.js';

class TgCredentials extends React.Component {
  constructor(props) {
    super(props);
    this.formOnChange = this.formOnChange.bind(this);
    this.state = this.getDefaultFormState();
  }

  async localStorageStateFetch() {
    // Delayed loading for custom device info (local storage) data for now
    // rather than in the main credentials routing/loading, since it's
    // vendor-specific logic.
    try {
      const acc = this.props.selectedAccount;
      const { mainDomainVal, img9Val } = await utils.getSnailMetrixLocalStorage(
        acc.vendorAccountId
      );
      this.setState({
        localStorageLoaded: true,
        localStorageMainDomainVal: mainDomainVal,
        localStorageImg9Val: img9Val,
      });
    } catch (err) {
      this.setState({
        localStorageErrorMessage:
          'Failed to load Target device info. Please refresh and try again.',
      });
    }
  }

  async storeSelectionStateFetch() {
    try {
      this.setState({
        storeSelectionLoaded: false,
        storeSelectionData: null,
      });
      const acc = this.props.selectedAccount;
      const { tgStores } = await utils.getTargetStoreSelection(
        acc.vendorAccountId
      );
      this.setState({
        storeSelectionLoaded: true,
        storeSelectionData: tgStores,
      });
    } catch (err) {
      this.setState({
        storeSelectionErrorMessage:
          'Failed to load Target store selection info. Please refresh and try again.',
      });
    }
  }

  getDefaultFormState() {
    return {
      isFilledOut: null,
      vaStatus: null,
      email: null,
      password: null,
      ccCode: null,
      userZipCode: null,
      touched: false,
      saving: false,
      errorMessage: null,
      successMessage: null,

      // Test order
      testOrderUrl: null,
      testOrderStoreId: null,
      testOrderTouched: false,
      testOrderErrorMessage: null,
      testOrderSuccessMessage: null,
      testOrderSaving: false,

      // Snailmetrix local storage
      localStorageLoaded: null,
      localStorageMainDomainVal: null,
      localStorageImg9Val: null,
      localStorageErrorMessage: null,
      localStorageSuccessMessage: null,
      localStorageSaving: false,
      localStorageTouched: false,

      // Store selection
      storeSelectionLoaded: false,
      storeSelectionData: null,
      storeSelectionErrorMessage: null,
      storeSelectionSaving: false,
      storeSelectionTouched: false,
    };
  }

  async componentDidMount() {
    if (!utils.isPC()) {
      this.setState({
        saving: false,
        errorMessage:
          'Please use a non-mobile device to update your credentials. Use the computer you usually browse Target with.',
      });
      return;
    }
    if (!this.props.credentialsLoading) {
      await this.updateState();
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.didSelectedCredChange(this.props, prevProps)) {
      await this.updateState();
    }
  }

  async updateState() {
    const acc = this.props.selectedAccount;
    if (!acc) {
      return this.setState({
        ...this.getDefaultFormState(),
        errorMessage:
          'Fill out all fields so the bot can perform autonomously. Your data is ONLY used for checking out on your behalf.',
      });
    }

    const { isFilledOut, email, userZipCode, vaStatus } = acc;
    this.setState({
      ...this.getDefaultFormState(),
      vaStatus,
      email,
      userZipCode,
      isFilledOut,
      errorMessage: isFilledOut
        ? null
        : 'Fill out all fields so the bot can perform autonomously. Your data is ONLY used for checking out on your behalf.',
    });

    this.localStorageStateFetch();
    this.storeSelectionStateFetch();
  }

  handleSave = async () => {
    this.setState({
      saving: true,
      errorMessage: null,
      successMessage: null,
    });

    let { email, password, ccCode, userZipCode, vaStatus } = this.state;

    if (email) {
      email = email.trim();
    }

    if (!utils.isPC()) {
      this.setState({
        saving: false,
        errorMessage:
          'Error: Please use a non-mobile device to update your credentials. Use the computer you usually browse Target with.',
      });
      return;
    }

    let vendorAccountId = null;
    let vaType = undefined;

    if (this.props.selectedAccount) {
      vendorAccountId = this.props.selectedAccount.vendorAccountId;
    }
    if (this.props.selectedCred) {
      vaType = this.props.selectedCred.vaType;
    }

    const { account, errorMessage } = await utils.putCredentials({
      vendor: 'target',
      vaStatus,
      vaType,
      vendorAccountId,
      email,
      password,
      ccCode,
      userZipCode,
      userAgent: utils.getUserAgent(),
    });
    if (account) {
      const { isFilledOut, vendorAccountId: returnedVendorAccountId } = account;
      this.setState({
        touched: false,
        saving: false,
        isFilledOut,
        successMessage:
          'Credentials saved. If you inputted new information, you should receive a Discord webhook notification once the bot logs into your account (it can take up to an hour).',
      });
      await this.props.updateCredentials({
        vaIdToSelectAfter: returnedVendorAccountId,
      });

      // Reload target stores selection view in case zip code was changed.
      this.storeSelectionStateFetch();

      // Run collector on successful credential save.
      await utils.sleep(2000); // Hack to make sure VA is already created for first time setup
      utils.injectSnailMetrix({
        source: 'credentials',
        vendorAccountId: returnedVendorAccountId,
      });
    } else {
      this.setState({
        errorMessage,
        saving: false,
      });
    }
  };

  renderSaveButton = () => {
    const color = this.state.touched ? 'warning' : 'primary';
    const spinner = <Spinner className="ml-1" size="sm" color="warning" />;
    const saveButton = (
      <Button
        disabled={!this.state.touched}
        color={color}
        size="lg"
        type="button"
        className="ml-3"
        onClick={this.handleSave}
      >
        Save Changes
      </Button>
    );
    return (
      <>
        {this.props.credentialsLoading ? null : saveButton}
        {this.state.saving ? spinner : null}
      </>
    );
  };

  async formOnChange(e) {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    this.setState({
      [inputName]: inputValue,
      touched: true,
    });
  }

  testOrderFormOnChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    this.setState({
      [inputName]: inputValue,
      testOrderTouched: true,
    });
  };

  localStorageFormOnChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value;
    this.setState({
      [inputName]: inputValue,
      localStorageTouched: true,
    });
  };

  renderErrors() {
    if (this.state.errorMessage) {
      return <Alert color="danger">{this.state.errorMessage}</Alert>;
    }
    if (this.state.successMessage) {
      return <Alert color="success">{this.state.successMessage}</Alert>;
    }
  }

  handleCheckboxChange() {
    const newStatus =
      this.state.vaStatus === 'active' ? 'user_disabled' : 'active';
    this.setState({
      vaStatus: newStatus,
      touched: true,
    });
  }

  async handleStoreSelectionSubmit() {
    const selectedAccount = this.props.selectedAccount;
    if (!selectedAccount) return;
    this.setState({
      storeSelectionSaving: true,
      storeSelectionErrorMessage: null,
    });

    const { tgStores, errorMessage } = await utils.putTargetStoreSelection({
      vendorAccountId: selectedAccount.vendorAccountId,
      tgStores: this.state.storeSelectionData || [],
    });

    if (errorMessage) {
      this.setState({
        storeSelectionSaving: false,
        storeSelectionErrorMessage: errorMessage,
        // Ensure touched is true to indicate changes are unsaved
        storeSelectionTouched: true,
      });
    } else {
      this.setState({
        storeSelectionSaving: false,
        storeSelectionTouched: false,
        storeSelectionData: tgStores,
      });
    }
  }

  renderStoreSelectionBtn() {
    if (!this.state.storeSelectionData?.length) {
      return null;
    }
    const color = this.state.storeSelectionTouched ? 'warning' : 'primary';
    const saveButton = (
      <Button
        disabled={!this.state.storeSelectionTouched}
        color={color}
        size="sm"
        outline
        type="button"
        className="ml-2"
        onClick={this.handleStoreSelectionSubmit.bind(this)}
      >
        Save Changes
      </Button>
    );
    return saveButton;
  }

  renderStoreSelectionSpinner() {
    const spinner = <Spinner className="ml-2 mb-1" size="sm" color="warning" />;
    let msg = null;
    if (!this.state.storeSelectionLoaded) {
      msg = 'Loading stores...';
    } else if (this.state.storeSelectionSaving) {
      msg = 'Saving...';
    }

    if (msg) {
      return (
        <>
          {spinner} <span className="ml-2 small text-gray">{msg}</span>
        </>
      );
    }
    return null;
  }

  renderStoreCountEnabled() {
    const isStoreDataLoaded = this.state.storeSelectionLoaded;
    if (!isStoreDataLoaded) {
      return;
    }

    const storeData = this.state.storeSelectionData || [];
    const enabledStoreCount = storeData.filter((store) => !store.disabled)
      .length;
    const totalStoreCount = storeData.length;
    const unsavedSuffix = this.state.storeSelectionTouched ? '(unsaved)' : '';

    return (
      <span className="text-gray mt-1 mr-3 float-right small">
        {' '}
        {enabledStoreCount}/{totalStoreCount} stores enabled {unsavedSuffix}
      </span>
    );
  }

  handleStoreSliderToggle(store) {
    store.disabled = !store.disabled;
    this.setState({
      storeSelectionTouched: true,
    });
  }

  renderStoreRows() {
    const isStoreDataLoaded = this.state.storeSelectionLoaded;
    const hasStoreData = !!this.state.storeSelectionData?.length;
    if (!isStoreDataLoaded) {
      return;
    }

    const noStoresDisplay = (
      <tr key="store-selection-empty">
        <td colSpan={4} className="text-center p-3">
          <b>
            Please check back in a short period of time for stores near your ZIP
            code to be populated.
            <br />
            If there are still no results, it may mean there are no Targets near
            your ZIP code.
          </b>
        </td>
      </tr>
    );

    // We've loaded stores, but nothing is showing up for this particular VA, so display
    // an informational message to the user.
    if (!hasStoreData) {
      return noStoresDisplay;
    }

    // Data received should already be sorted by distance -- do not sort again, just
    // process in order.
    return this.state.storeSelectionData.map((store) => {
      const { distance, storeId, address, name, disabled } = store;
      const mapsUrl = utils.createGoogleMapsSearchUrl('Target', address);
      const storeAnchor = name ? (
        <a href={utils.createTargetStoreUrl(storeId)} target="_blank">
          {name}
        </a>
      ) : (
        'N/A'
      );

      return (
        <tr key={storeId}>
          <td>
            <label
              className="custom-toggle"
              style={{ marginBottom: '0px', marginTop: '6px' }}
            >
              <input
                checked={!disabled}
                onChange={() => this.handleStoreSliderToggle(store)}
                type="checkbox"
              />
              <span className="custom-toggle-slider rounded-circle" />
            </label>
          </td>
          <td>{distance} mi</td>
          <td>{storeAnchor}</td>
          <td>
            <a href={mapsUrl} target="_blank">
              {address}
            </a>
          </td>
        </tr>
      );
    });
  }

  renderStoreSelection() {
    return (
      <Row>
        <Col className="mt-3 order-xl-1" xl="8">
          <Card className="bg-secondary shadow mb-4">
            <CardHeader className="bg-secondary border-0 sticky z-999">
              <h3 className="mb-0">
                <span>Target Store Selection</span>
                {this.renderStoreSelectionBtn()}
                {this.renderStoreSelectionSpinner()}
                {this.renderStoreCountEnabled()}
              </h3>
              <div className="text-gray">
                Select the stores you want to pick up items from (some items are
                pickup only). We recommend enabling EVERY store you're willing
                to go to, since this will maximize your chance for checkouts.
              </div>
              {this.state.storeSelectionErrorMessage ? (
                <Alert color="danger" className="mt-2">
                  {this.state.storeSelectionErrorMessage}
                </Alert>
              ) : null}
            </CardHeader>
            <Table
              className="align-items-center table table-flush overflow-hidden"
              responsive
            >
              <thead>
                <tr>
                  <th scope="col" style={{ width: '10%' }}>
                    Enabled
                  </th>
                  <th scope="col" style={{ width: '10%' }}>
                    Distance
                  </th>
                  <th scope="col" style={{ width: '30%' }}>
                    Store Name
                  </th>
                  <th scope="col" style={{ width: '50%' }}>
                    Address
                  </th>
                </tr>
              </thead>
              <tbody>{this.renderStoreRows()}</tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    );
  }

  renderLocalStorageAlert() {
    return (
      <Alert color="danger">
        ⚠️{' '}
        <b>
          <u>WARNING</u>
        </b>
        : Only modify the section below if you are experiencing repeated order
        cancellations on Target. <b>LEAVE THIS ALONE OTHERWISE.</b>
      </Alert>
    );
  }

  renderLocalStorageBtn = () => {
    const color = this.state.localStorageTouched ? 'warning' : 'primary';
    const spinner = <Spinner className="ml-1" size="sm" color="warning" />;
    const saveButton = (
      <Button
        disabled={
          !this.state.localStorageTouched || this.state.localStorageSaving
        }
        color={color}
        size="lg"
        type="button"
        className="ml-3"
        onClick={this.handleLocalStorageSubmit}
      >
        Save Changes
      </Button>
    );
    return (
      <>
        {this.props.credentialsLoading || !this.state.localStorageLoaded
          ? null
          : saveButton}
        {this.state.localStorageSaving ? spinner : null}
      </>
    );
  };

  renderLocalStorageCollect() {
    return (
      <Form>
        <hr className="mt-5" />
        {this.state.localStorageErrorMessage ? (
          <Alert color="danger">{this.state.localStorageErrorMessage}</Alert>
        ) : null}
        {this.state.localStorageSuccessMessage ? (
          <Alert color="success">{this.state.localStorageSuccessMessage}</Alert>
        ) : null}
        {this.renderUseMainDeviceAlert()}
        <h5 className="mb-4">
          <div className="heading-small text-muted">Target Device Info</div>
          <div className="mb-2">
            When you update your credentials, we collect info about your browser
            to mimic your device when checking out products to reduce the chance
            Target will cancel your orders.
          </div>
          <div className="mb-2">
            For some users, this may not be sufficient. You can further prevent
            order cancellations by providing additional tracking information
            Target has about you.
          </div>
          <div className="mb-2">
            <b>Follow this guide carefully:</b>{' '}
            <a
              href="https://docs.snailbot.io/finding-device-info-target"
              target="_blank"
            >
              https://docs.snailbot.io/finding-device-info-target
            </a>
          </div>
        </h5>
        <div className="pl-lg-4">
          <Row>
            <Col lg="12">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="input-localStorageMainDomainVal"
                >
                  Your <b>www.target.com</b> Value
                </label>
                <Input
                  name="localStorageMainDomainVal"
                  value={this.state.localStorageMainDomainVal || ''}
                  maxLength="46"
                  className="form-control-alternative"
                  id="input-localStorageMainDomainVal"
                  placeholder="e.g. aaa00000000000000000000000000000"
                  type="text"
                  onChange={this.localStorageFormOnChange}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="input-localStorageImg9Val"
                >
                  Your <b>img9.target.com</b> Value
                </label>
                <Input
                  name="localStorageImg9Val"
                  value={this.state.localStorageImg9Val || ''}
                  maxLength="46"
                  className="form-control-alternative"
                  id="input-localStorageImg9Val"
                  placeholder="e.g. aaa00000000000000000000000000000"
                  type="text"
                  onChange={this.localStorageFormOnChange}
                />
              </FormGroup>
              <Row>{this.renderLocalStorageBtn()}</Row>
            </Col>
          </Row>
        </div>
      </Form>
    );
  }

  handleLocalStorageSubmit = async () => {
    const selectedAccount = this.props.selectedAccount;
    if (!selectedAccount) return;
    this.setState({
      localStorageSaving: true,
      localStorageErrorMessage: null,
      localStorageSuccessMessage: null,
    });

    const { errorMessage } = await utils.putSnailMetrixLocalStorage({
      mainDomainVal: this.state.localStorageMainDomainVal,
      img9Val: this.state.localStorageImg9Val,
      vendorAccountId: selectedAccount.vendorAccountId,
    });

    if (errorMessage) {
      this.setState({
        localStorageSaving: false,
        localStorageErrorMessage: errorMessage,
        localStorageTouched: false,
      });
    } else {
      this.setState({
        localStorageSaving: false,
        localStorageSuccessMessage: 'Target device info updated successfully.',
        localStorageTouched: false,
      });
    }
  };

  renderEnabledCheckbox() {
    return this.state.vaStatus ? (
      <Row>
        <Col lg="12">
          <div className="custom-control custom-checkbox mb-4">
            <input
              className="custom-control-input"
              checked={this.state.vaStatus === 'active'}
              id="customCheck2"
              type="checkbox"
              onChange={() => this.handleCheckboxChange()}
            />
            <label className="custom-control-label" htmlFor="customCheck2">
              Enabled
            </label>
          </div>
        </Col>
      </Row>
    ) : null;
  }

  renderTestOrder() {
    return (
      <Form>
        <hr className="mt-5" />
        {this.renderUseMainDeviceAlert()}
        {this.state.testOrderErrorMessage ? (
          <Alert color="danger">{this.state.testOrderErrorMessage}</Alert>
        ) : null}
        {this.state.testOrderSuccessMessage ? (
          <Alert color="success">{this.state.testOrderSuccessMessage}</Alert>
        ) : null}
        <h5 className="mb-4">
          <div className="heading-small text-muted">Test Order Request</div>
          <div>
            Use this to verify the bot can successfully make purchases with your
            account. Note that test orders are delayed and not indicative of the
            bot's performance. Provide the URL to the Target product page of the
            item you want to purchase.
          </div>
          <b>Note: Product must be in stock and sold by Target.com.</b>
        </h5>
        <div className="pl-lg-4">
          <Row>
            <Col lg="12">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="input-testOrderLink"
                >
                  Target Product Page URL
                </label>
                <Input
                  name="testOrderUrl"
                  value={this.state.testOrderUrl || ''}
                  maxLength="2000"
                  className="form-control-alternative"
                  id="input-testOrderUrl"
                  placeholder="https://www.target.com/p/2-wood-pencils-24ct-up-38-up-8482/-/A-10805587"
                  type="text"
                  onChange={this.testOrderFormOnChange}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <FormGroup>
                <label
                  className="form-control-label"
                  htmlFor="input-testOrderStoreId"
                >
                  Target Store ID (fill out only for Pickup orders) -{' '}
                  <a
                    href="https://docs.snailbot.io/finding-store-id"
                    target="_blank"
                  >
                    How to find Store ID?
                  </a>
                </label>
                <Input
                  name="testOrderStoreId"
                  value={this.state.testOrderStoreId || ''}
                  max="1000000"
                  className="form-control-alternative"
                  id="input-testOrderStoreId"
                  placeholder="1234"
                  type="number"
                  onChange={this.testOrderFormOnChange}
                />
              </FormGroup>
              <Row>{this.renderTestOrderBtn()}</Row>
            </Col>
          </Row>
        </div>
      </Form>
    );
  }

  renderTestOrderBtn = () => {
    const color = this.state.testOrderTouched ? 'warning' : 'primary';
    const spinner = <Spinner className="ml-1" size="sm" color="warning" />;
    const saveButton = (
      <Button
        disabled={!this.state.testOrderTouched || this.state.testOrderSaving}
        color={color}
        size="lg"
        type="button"
        className="ml-3"
        onClick={this.handleTestOrderSubmit}
      >
        Request Test Order
      </Button>
    );
    return (
      <>
        {this.props.credentialsLoading ? null : saveButton}
        {this.state.testOrderSaving ? spinner : null}
      </>
    );
  };

  renderUseMainDeviceAlert = () => {
    return (
      <Alert color="warning">
        👉{' '}
        <b>
          <u>IMPORTANT</u>
        </b>
        : When updating this section, make sure to use the <b>SAME COMPUTER</b>{' '}
        and <b>BROWSER</b> you use when manually purchasing items on Target
        normally. Your orders on Target may be cancelled if you do not do so.
      </Alert>
    );
  };

  handleTestOrderSubmit = async () => {
    const selectedAccount = this.props.selectedAccount;
    if (!selectedAccount) return;
    this.setState({
      testOrderSaving: true,
      testOrderErrorMessage: null,
      testOrderSuccessMessage: null,
    });

    const url = this.state.testOrderUrl;
    const tcin = utils.getTcinFromDPUrl(url);
    if (!tcin) {
      return this.setState({
        testOrderSaving: false,
        testOrderErrorMessage:
          'Product page URL is in the wrong format. Make sure it is like https://www.target.com/p/2-wood-pencils-24ct-up-38-up-8482/-/A-10805587',
      });
    }

    const { errorMessage } = await utils.submitTestOrder({
      vendor: 'target',
      url,
      storeId: this.state.testOrderStoreId,
      vendorAccountId: selectedAccount.vendorAccountId,
    });
    if (errorMessage) {
      this.setState({
        testOrderSaving: false,
        testOrderErrorMessage: errorMessage,
        testOrderTouched: false,
      });
    } else {
      // Run collector on successful test order submit.
      utils.injectSnailMetrix({
        source: 'testorder',
        vendorAccountId: selectedAccount.vendorAccountId,
      });

      this.setState({
        testOrderSaving: false,
        testOrderSuccessMessage:
          'Request submitted. Check your webhook notifications for updates.',
        testOrderTouched: false,
      });
    }
  };

  render() {
    if (!this.props.selectedCred) return 'No selected instance.';
    const vendor = this.props.selectedCred.vendor;
    return (
      <>
        {/*<UserHeader />*/}
        {/* Page content */}
        <Container className="mt--7" fluid>
          <Row>
            <Col className="order-xl-1" xl="8">
              <Card className="bg-secondary shadow">
                <CardHeader className="bg-white border-0">
                  <Row className="align-items-center">
                    <Col xs="8">
                      <h3 className="mb-0">
                        {`${utils.titleCase(vendor)} Credentials`}
                        {this.props.credentialsLoading ? (
                          <Spinner
                            className="ml-2 mb-1"
                            size="sm"
                            color="warning"
                          />
                        ) : null}
                      </h3>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {this.renderUseMainDeviceAlert()}
                  <Alert color="default">
                    Hold up! Make sure to read the{' '}
                    <a href="https://docs.snailbot.io/target" target="_blank">
                      Target Setup Guide
                    </a>{' '}
                    before entering your credentials!
                  </Alert>
                  {this.renderErrors()}
                  <Form>
                    <h5 className="mb-4">
                      <div className="heading-small text-muted">
                        Target Login Info
                      </div>
                      <div>
                        Your Target login info is required for the bot to make
                        purchases on your behalf. Your info is encrypted using
                        the latest security standards.
                      </div>
                      <div>
                        Read this carefully:{' '}
                        <a
                          href="https://docs.snailbot.io/target"
                          target="_blank"
                        >
                          Target Setup Guide
                        </a>
                      </div>
                    </h5>
                    <div className="pl-lg-4">
                      <Row>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-email"
                            >
                              Email
                            </label>
                            <Input
                              name="email"
                              maxLength="100"
                              value={this.state.email || ''}
                              className="form-control-alternative"
                              id="input-email"
                              placeholder="snail@email.com"
                              type="text"
                              onChange={this.formOnChange}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg="6">
                          <FormGroup>
                            <label
                              className="form-control-label"
                              htmlFor="input-password"
                            >
                              Password
                            </label>
                            <Input
                              name="password"
                              autoComplete="off"
                              maxLength="100"
                              value={this.state.password || ''}
                              className="form-control-alternative"
                              // value="lucky.jesse"
                              id="input-password"
                              placeholder={
                                this.state.isFilledOut
                                  ? '<HIDDEN>'
                                  : 'ballsack123'
                              }
                              type="text"
                              onChange={this.formOnChange}
                            />
                            {/*<Input placeholder="ballsack123" type="password"/>*/}
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="12">
                          <FormGroup>
                            <label className="form-control-label">
                              Default Payment Card CVV
                              <div>
                                <small>
                                  Provide the 3-4 digit security code of the
                                  default payment card you set up on your Target
                                  account, since it may be required during the
                                  checkout process. Your info is encrypted using
                                  the latest security standards.
                                </small>
                              </div>
                            </label>
                            <Input
                              name="ccCode"
                              maxLength="4"
                              value={this.state.ccCode || ''}
                              className="form-control-alternative"
                              placeholder={
                                this.state.isFilledOut ? '<HIDDEN>' : '555'
                              }
                              type="number"
                              onChange={this.formOnChange}
                            />
                          </FormGroup>
                        </Col>
                        <Col lg="12">
                          <FormGroup>
                            <label className="form-control-label">
                              Your ZIP Code
                              <div>
                                <small>
                                  Provide your ZIP code so we can purchase
                                  pickup-only items from your nearby Target
                                  stores (20 mile radius). Certain in-demand
                                  items (e.g. consoles) can only be picked up
                                  from a store after purchase.
                                </small>
                              </div>
                            </label>
                            <Input
                              name="userZipCode"
                              maxLength="5"
                              value={this.state.userZipCode || ''}
                              className="form-control-alternative"
                              placeholder="12345"
                              type="number"
                              onChange={this.formOnChange}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg="12">
                          {this.renderEnabledCheckbox()}
                          <Row>{this.renderSaveButton()}</Row>
                        </Col>
                      </Row>
                    </div>
                  </Form>
                  {this.renderLocalStorageCollect()}
                  {this.state.vaStatus === 'active'
                    ? this.renderTestOrder()
                    : null}
                </CardBody>
              </Card>
            </Col>
          </Row>
          {this.state.vaStatus === 'active'
            ? this.renderStoreSelection()
            : null}
        </Container>
      </>
    );
  }
}

export default TgCredentials;
